import {
  AUTH_EVENTS,
  AuthError,
  MissingIdentityError,
  acceptInvite,
  getUser,
  handleAuthCallback,
  login as identityLogin,
  loginWithGoogle as identityGoogleLogin,
  logout as identityLogout,
  onAuthChange,
  requestPasswordRecovery,
  signup as identitySignup,
  updateUser,
} from './auth-local.js'

let currentUser = null;
let inviteToken = null;
let currentPlan = 'free';

/* ---------- free-plan quota: 5 AI analyses / month (enforced on-device) ---------- */
const FREE_MONTHLY_LIMIT = 5;
function quotaKey() {
  const who = (currentUser && currentUser.email ? currentUser.email : 'anon').toLowerCase();
  const d = new Date();
  return 'scamlens_quota_' + who + '_' + d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}
function quotaUsed() {
  try { return parseInt(localStorage.getItem(quotaKey()) || '0', 10) || 0 } catch { return 0 }
}
function bumpQuota() {
  try { localStorage.setItem(quotaKey(), String(quotaUsed() + 1)) } catch {}
}
function quotaLeft() { return Math.max(0, FREE_MONTHLY_LIMIT - quotaUsed()) }
function renderQuota() {
  const el = document.getElementById('quotaNote');
  if (!el) return;
  if (currentPlan !== 'free') { el.style.display = 'none'; return }
  el.style.display = 'block';
  const left = quotaLeft();
  const link = '<a style="cursor:pointer;color:var(--blue)" onclick="go(\'pricing\')">';
  el.innerHTML = left > 0
    ? 'Free plan: <b>' + left + ' of ' + FREE_MONTHLY_LIMIT + '</b> analyses left this month. ' + link + 'Upgrade for unlimited</a>'
    : 'You\'ve used your <b>' + FREE_MONTHLY_LIMIT + ' free analyses</b> this month. ' + link + 'Upgrade to Plus for unlimited</a>';
}

/* Billing API (Cloudflare Worker, free tier). */
const API_BASE = 'https://scamlens-api.umutgeo4.workers.dev';

/* ---------- navigation ---------- */

const protectedPages = ['dashboard', 'settings'];

function go(id) {
  if (protectedPages.includes(id) && !currentUser) id = 'login';
  document.querySelectorAll('.page').forEach(p => p.classList.remove('active'));
  document.getElementById(id).classList.add('active');
  window.scrollTo(0, 0);
  const footer = document.getElementById('siteFooter');
  if (footer) footer.classList.toggle('hidden', id === 'dashboard' || id === 'settings');
  if (id === 'dashboard') { refreshPlan(); dashGo('overview'); }
  if (id === 'settings') fillSettings();
}
function demo() { go(currentUser ? 'dashboard' : 'signup') }
function goAnalyze() {
  if (currentUser) { go('dashboard'); dashGo('analyze'); }
  else go('signup');
}
function dashGo(view) {
  document.querySelectorAll('.dashView').forEach(v => v.classList.remove('active'));
  const target = document.getElementById('view-' + view);
  if (target) target.classList.add('active');
  document.querySelectorAll('.sidebar .sideItem[data-view]').forEach(s => s.classList.toggle('active', s.dataset.view === view));
  if (view === 'analyze') renderQuota();
  window.scrollTo(0, 0);
}
function howItWorks() { go('home'); setTimeout(() => { const el = document.getElementById('howPreview'); if (el) el.scrollIntoView({behavior:'smooth'}) }, 60) }

/* ---------- form feedback ---------- */

function setMsg(scope, text, kind) {
  const el = document.getElementById(scope + 'Msg');
  if (!el) return;
  el.textContent = text || '';
  el.className = text ? 'msg show ' + (kind || 'error') : 'msg';
}
function busy(button, label) {
  if (!button) return;
  if (label) {
    if (button.dataset.idle === undefined) button.dataset.idle = button.innerHTML;
    button.innerHTML = label;
    button.disabled = true;
  } else {
    if (button.dataset.idle !== undefined) button.innerHTML = button.dataset.idle;
    button.disabled = false;
  }
}

// AuthError.from() drops the HTTP status for browser-side failures, so fall back
// to the status carried by the underlying GoTrue error.
function statusOf(error) {
  if (error && error.status) return error.status;
  if (error && error.cause && error.cause.status) return error.cause.status;
  return undefined;
}

function describeAuthError(error) {
  if (error instanceof MissingIdentityError) {
    return 'Netlify Identity is not enabled for this site yet. Enable it in the Netlify project settings, then try again.';
  }
  if (!(error instanceof AuthError)) return 'Something went wrong. Please try again.';
  const message = error.message || '';
  // Never say which half of the credentials was wrong — that leaks which email
  // addresses have accounts.
  if (statusOf(error) === 401 || /invalid_grant|password invalid|no user found/i.test(message)) {
    return 'Invalid email or password.';
  }
  if (/not confirmed/i.test(message)) {
    return 'Confirm your email address first — check your inbox for the confirmation link.';
  }
  if (/already registered/i.test(message)) {
    return 'That email address is already registered. Log in instead.';
  }
  if (statusOf(error) === 403) return 'Sign-ups are currently disabled for ScamLens.';
  // GoTrue prefixes some errors machine-style, e.g. "invalid_request: ...".
  return message.replace(/^[a-z_]+:\s*/, '') || 'Something went wrong. Please try again.';
}

/* ---------- auth state ---------- */

function displayName(user) {
  if (user.name) return user.name;
  return user.email ? user.email.split('@')[0] : 'there';
}

function applyUser(user) {
  currentUser = user || null;
  document.getElementById('navGuest').classList.toggle('hidden', Boolean(currentUser));
  document.getElementById('navUser').classList.toggle('hidden', !currentUser);
  if (!currentUser) return;
  const name = displayName(currentUser);
  document.getElementById('username').textContent = name;
  document.getElementById('helloName').textContent = name;
  document.getElementById('userEmail').textContent = currentUser.email || '';
  document.getElementById('userAvatar').textContent = name.charAt(0).toUpperCase();
}

function resetDashboard() {
  document.getElementById('resultBox').style.display = 'none';
  document.getElementById('analysisText').value = '';
  document.getElementById('uploadName').textContent = '';
  pendingImage = null;
  paintHistory();
  paintTotals();
  setMsg('dash', '');
}

/* ---------- email + password ---------- */

async function handleLogin(event) {
  event.preventDefault();
  const button = document.getElementById('loginSubmit');
  const email = document.getElementById('loginEmail').value.trim();
  const password = document.getElementById('loginPassword').value;
  if (!email || !password) { setMsg('login', 'Enter your email address and password.'); return }
  setMsg('login', '');
  busy(button, 'Logging in…');
  try {
    applyUser(await identityLogin(email, password));
    document.getElementById('loginForm').reset();
    go('dashboard');
  } catch (error) {
    setMsg('login', describeAuthError(error));
  } finally {
    busy(button, null);
  }
}

async function handleSignup(event) {
  event.preventDefault();
  const button = document.getElementById('signupSubmit');
  const name = document.getElementById('signupName').value.trim();
  const email = document.getElementById('signupEmail').value.trim();
  const password = document.getElementById('signupPassword').value;
  if (!email || !password) { setMsg('signup', 'Enter an email address and a password.'); return }
  if (password.length < 8) { setMsg('signup', 'Choose a password of at least 8 characters.'); return }
  setMsg('signup', '');
  busy(button, 'Creating account…');
  try {
    const created = await identitySignup(email, password, name ? {full_name: name} : undefined);
    document.getElementById('signupForm').reset();
    if (created.confirmedAt) {
      // Autoconfirm is on, so log in immediately to persist a browser session.
      applyUser(await identityLogin(email, password));
      setMsg('dash', 'Welcome to ScamLens. Your account is ready.', 'ok');
      go('dashboard');
    } else {
      setMsg('signup', 'Almost there — we sent a confirmation link to ' + email + '. Confirm your email to activate your account.', 'ok');
    }
  } catch (error) {
    setMsg('signup', describeAuthError(error));
  } finally {
    busy(button, null);
  }
}

async function handleForgotPassword() {
  const link = document.getElementById('forgotLink');
  const email = document.getElementById('loginEmail').value.trim();
  if (!email) { setMsg('login', 'Enter your email address first, then choose “Forgot password?”.'); return }
  const sent = 'If an account exists for ' + email + ', a password reset link is on its way.';
  link.disabled = true;
  try {
    await requestPasswordRecovery(email);
    setMsg('login', sent, 'ok');
  } catch (error) {
    // An unknown address is answered the same way as a known one.
    if (statusOf(error) === 404 || /not found/i.test(error.message || '')) {
      setMsg('login', sent, 'ok');
    } else {
      setMsg('login', describeAuthError(error));
    }
  } finally {
    link.disabled = false;
  }
}

async function handleNewPassword(event) {
  event.preventDefault();
  const button = document.getElementById('resetSubmit');
  const password = document.getElementById('resetPassword').value;
  const confirmation = document.getElementById('resetConfirm').value;
  if (password.length < 8) { setMsg('reset', 'Choose a password of at least 8 characters.'); return }
  if (password !== confirmation) { setMsg('reset', 'The two passwords do not match.'); return }
  setMsg('reset', '');
  busy(button, 'Saving…');
  try {
    let user;
    if (inviteToken) {
      user = await acceptInvite(inviteToken, password);
      inviteToken = null;
    } else {
      await updateUser({password});
      user = currentUser;
    }
    applyUser(await getUser() || user);
    document.getElementById('resetForm').reset();
    setMsg('dash', 'Your password has been updated.', 'ok');
    go('dashboard');
  } catch (error) {
    setMsg('reset', describeAuthError(error));
  } finally {
    busy(button, null);
  }
}

/* ---------- Google sign-in (Google Identity Services) ---------- */
const GOOGLE_CLIENT_ID = '944456103186-kcv74sg089meg9kkd1jggfda0p2o3t45.apps.googleusercontent.com';

function activeAuthScope() {
  const s = document.getElementById('signup');
  return s && s.classList.contains('active') ? 'signup' : 'login';
}

async function onGoogleCredential(response) {
  const scope = activeAuthScope();
  try {
    await identityGoogleLogin(response && response.credential);
    setMsg(scope, 'Signed in with Google.', 'ok');
    go('dashboard');
  } catch (error) {
    setMsg(scope, describeAuthError(error));
  }
}

function initGoogle(retries = 10) {
  const gid = window.google && google.accounts && google.accounts.id;
  if (!gid) {
    // The Google script loads async — retry briefly, then give up silently
    // (email login keeps working even if Google is blocked/offline).
    if (retries > 0) setTimeout(() => initGoogle(retries - 1), 500);
    return;
  }
  try {
    google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: onGoogleCredential });
    const base = { theme: 'outline', size: 'large', width: 366, shape: 'rectangular' };
    const loginBtn = document.getElementById('gbtn-login');
    const signupBtn = document.getElementById('gbtn-signup');
    if (loginBtn) google.accounts.id.renderButton(loginBtn, { ...base, text: 'continue_with' });
    if (signupBtn) google.accounts.id.renderButton(signupBtn, { ...base, text: 'signup_with' });
  } catch {
    // Google button unavailable — email login still works.
  }
}

async function logout() {
  try {
    await identityLogout();
  } catch (error) {
    // The local session is cleared either way; drop the user back to the marketing site.
  }
  applyUser(null);
  resetDashboard();
  go('home');
}

/* ---------- free AI analysis (no key, no credits) ---------- */
// The AI endpoint is keyless and public, so the browser calls it directly.
// This also avoids Netlify Functions' 10-second timeout on the free tier.
const AI_URL = 'https://text.pollinations.ai/openai';

const AI_SYSTEM_PROMPT = [
  'You are ScamLens, an expert fraud analyst. The user will share a message, email,',
  'job offer, or marketplace conversation (and possibly a screenshot) that they suspect',
  'might be a scam. Analyze it carefully for scam indicators: urgency/pressure tactics,',
  'requests for money or unusual payment methods (gift cards, crypto, wire), requests for',
  'sensitive info (passwords, SSN, verification codes), too-good-to-be-true promises,',
  'suspicious links, impersonation of officials/companies, avoidance of verification.',
  'Reply with ONLY valid JSON, no other text:',
  '{"score": <integer 0-100>, "label": "<LOWER RISK|SUSPICIOUS|HIGH RISK>", "reasons": ["<short reason>", ...]}',
  'Mapping: 0-39 LOWER RISK, 40-69 SUSPICIOUS, 70-100 HIGH RISK.',
  'Give 2-5 short plain-language reasons a normal person can understand.',
  'If it looks legitimate, say so honestly with a low score — never inflate.',
].join(' ');

function labelForScore(score) {
  return score >= 70 ? 'HIGH RISK' : score >= 40 ? 'SUSPICIOUS' : 'LOWER RISK';
}

function parseAIJson(raw) {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) throw new Error('No JSON in AI response');
  const obj = JSON.parse(raw.slice(start, end + 1));
  const score = Math.max(0, Math.min(100, Math.round(Number(obj.score) || 0)));
  const reasons = Array.isArray(obj.reasons) && obj.reasons.length
    ? obj.reasons.slice(0, 6).map(String)
    : ['The AI could not explain this result clearly — treat unexpected money requests with caution.'];
  return { score, reasons };
}

async function aiAnalyze(text, imageDataUrl) {
  const userContent = [];
  if (text) userContent.push({ type: 'text', text: 'Analyze this message:\n\n' + text });
  if (imageDataUrl) userContent.push({ type: 'image_url', image_url: { url: imageDataUrl } });
  if (!userContent.length) throw new Error('Empty input');

  const resp = await fetch(AI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'openai',
      messages: [
        { role: 'system', content: AI_SYSTEM_PROMPT },
        { role: 'user', content: userContent },
      ],
      temperature: 0.2,
      max_tokens: 450,
    }),
    signal: AbortSignal.timeout(45000),
  });
  if (!resp.ok) throw new Error('AI service returned HTTP ' + resp.status);
  const data = await resp.json();
  const raw = (data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) || '';
  return parseAIJson(raw);
}

const rules = [
  [['gift card','bitcoin','crypto','wire','processing fee','send money','payment'],25,'Requests money or an unusual form of payment.'],
  [['urgent','immediately','act now','final warning','suspended'],17,'Uses urgency or pressure to force a quick decision.'],
  [['password','social security','ssn','verification code','otp','security code'],30,'Requests sensitive personal or account information.'],
  [['winner','congratulations','selected','guaranteed','easy money'],15,'Uses unusually attractive promises or rewards.'],
  [['click','http','link'],9,'Asks you to follow a link that should be independently verified.'],
  [['cannot meet','agent will','pick it up'],12,'Avoids normal verification or direct interaction.']
];

function heuristicScore(text) {
  let t = (text || '').toLowerCase();
  let score = 8, rs = [];
  rules.forEach(r=>{if(r[0].some(w=>t.includes(w))){score+=r[1];rs.push(r[2])}});score=Math.min(98,score);
  if(!rs.length)rs.push('No major common scam patterns were detected. This is not a guarantee that the message is safe.','Always independently verify unexpected requests for money or personal information.');
  return { score, reasons: rs };
}

let pendingImage = null; // data URL of the uploaded screenshot (if any)

function handleImageSelect(input) {
  const file = input.files && input.files[0];
  const label = document.getElementById('uploadName');
  pendingImage = null;
  if (!file) { label.textContent = ''; return }
  if (!file.type.startsWith('image/')) { label.textContent = 'Please choose an image file.'; input.value = ''; return }
  if (file.size > 1.5 * 1024 * 1024) { label.textContent = 'Image is too large (max 1.5 MB).'; input.value = ''; return }
  const reader = new FileReader();
  reader.onload = () => { pendingImage = reader.result; label.textContent = 'Selected: ' + file.name };
  reader.readAsDataURL(file);
}

let lastResult = null; // {score, reasons} of the latest analysis, for the share card

function wrapLines(x, text, maxW) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const t = line ? line + ' ' + w : w;
    if (x.measureText(t).width > maxW && line) { lines.push(line); line = w }
    else line = t;
  }
  if (line) lines.push(line);
  return lines;
}

async function shareResult() {
  if (!lastResult) return;
  const { score, reasons } = lastResult;
  const label = score >= 70 ? 'HIGH RISK' : score >= 40 ? 'SUSPICIOUS' : 'LOWER RISK';
  const color = score >= 70 ? '#ff5d73' : score >= 40 ? '#ffcf5c' : '#35d99a';
  const W = 1080, H = 1350;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d');
  const bg = x.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0d1a2b'); bg.addColorStop(1, '#060d16');
  x.fillStyle = bg; x.fillRect(0, 0, W, H);
  // soft glow
  const glow = x.createRadialGradient(W/2, 420, 40, W/2, 420, 520);
  glow.addColorStop(0, color + '2e'); glow.addColorStop(1, 'transparent');
  x.fillStyle = glow; x.fillRect(0, 0, W, H);
  // brand
  x.textAlign = 'center';
  x.fillStyle = '#e8eef4'; x.font = '800 62px system-ui, sans-serif';
  x.fillText('ScamLens', W/2, 130);
  x.fillStyle = '#8b98a8'; x.font = '400 30px system-ui, sans-serif';
  x.fillText('Know Before You Trust', W/2, 182);
  // risk ring
  const cx = W/2, cy = 500, r = 200;
  x.lineWidth = 46; x.lineCap = 'round';
  x.strokeStyle = '#1d3247';
  x.beginPath(); x.arc(cx, cy, r, 0, Math.PI*2); x.stroke();
  x.strokeStyle = color;
  x.beginPath(); x.arc(cx, cy, r, -Math.PI/2, -Math.PI/2 + Math.PI*2*score/100); x.stroke();
  x.fillStyle = '#ffffff'; x.font = '800 118px system-ui, sans-serif';
  x.fillText(score + '%', cx, cy + 42);
  x.fillStyle = color; x.font = '800 42px system-ui, sans-serif';
  x.fillText(label, cx, cy + 168);
  // top reasons
  x.textAlign = 'left';
  x.fillStyle = '#8b98a8'; x.font = '700 28px system-ui, sans-serif';
  x.fillText('TOP WARNING SIGNS', 120, 850);
  x.fillStyle = '#c5d1dc'; x.font = '400 30px system-ui, sans-serif';
  let ry = 900;
  for (const t of reasons.slice(0, 3)) {
    const short = String(t).length > 110 ? String(t).slice(0, 107) + '…' : String(t);
    for (const ln of wrapLines(x, '•  ' + short, W - 240)) {
      if (ry > 1180) break;
      x.fillText(ln, 120, ry); ry += 46;
    }
    ry += 14;
  }
  // footer
  x.textAlign = 'center';
  x.fillStyle = '#5c6f83'; x.font = '400 27px system-ui, sans-serif';
  x.fillText('scamlensapp.com', W/2, H - 64);
  const blob = await new Promise(res => c.toBlob(res, 'image/png'));
  if (!blob) return;
  const file = new File([blob], 'scamlens-result.png', { type: 'image/png' });
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: 'ScamLens result', text: `ScamLens scored this message ${score}% — ${label}` });
      return;
    }
  } catch { /* user cancelled or share failed: fall through to download */ }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'scamlens-result.png';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

function renderAnalysis(score, reasons) {
  const color = score>=70?'var(--red)':score>=40?'var(--yellow)':'var(--green)';
  const label = score>=70?'HIGH RISK':score>=40?'SUSPICIOUS':'LOWER RISK';
  lastResult = { score, reasons: reasons.slice() };
  document.getElementById('resultBox').style.display='block';
  document.getElementById('riskNum').textContent=score+'%';
  document.getElementById('riskLabel').textContent=label;
  document.getElementById('riskLabel').style.color=color;
  document.getElementById('bigCircle').style.background=`conic-gradient(${color} 0 ${score*3.6}deg,#203449 ${score*3.6}deg)`;
  document.getElementById('reasons').innerHTML=reasons.map(x=>'<li>'+x+'</li>').join('');
  document.getElementById('resultBox').scrollIntoView({behavior:'smooth'});
  return label;
}

/* ---------- persistent history + totals (this browser) ---------- */

function loadHistory() {
  try { return JSON.parse(localStorage.getItem('scamlens_history') || '[]') } catch { return [] }
}
function historyItemHtml(h) {
  return `<div class="historyItem"><div><b>${h.title}</b><div class="muted" style="font-size:12px">${h.when}</div></div>` +
    `<span class="pill ${h.score>=70?'high':h.score>=40?'medium':'low'}">${h.score}% ${h.label}</span></div>`;
}
function paintHistory() {
  const items = loadHistory();
  const empty = '<p class="muted">No analyses yet. Your results will appear here.</p>';
  const full = document.getElementById('historyList');
  if (full) full.innerHTML = items.length ? items.map(historyItemHtml).join('') : empty;
  const recent = document.getElementById('recentList');
  if (recent) recent.innerHTML = items.length ? items.slice(0, 5).map(historyItemHtml).join('') : empty;
}
function pushHistory(score, label, title) {
  const items = loadHistory();
  items.unshift({ score, label, title, when: new Date().toLocaleString() });
  try { localStorage.setItem('scamlens_history', JSON.stringify(items.slice(0, 20))) } catch {}
  paintHistory();
}
function bumpTotals(score) {
  let t = { n: 0, h: 0 };
  try { t = JSON.parse(localStorage.getItem('scamlens_totals') || '{"n":0,"h":0}') } catch {}
  t.n++; if (score >= 70) t.h++;
  try { localStorage.setItem('scamlens_totals', JSON.stringify(t)) } catch {}
  document.getElementById('analysisCount').textContent = t.n;
  document.getElementById('highCount').textContent = t.h;
}
function paintTotals() {
  let t = { n: 0, h: 0 };
  try { t = JSON.parse(localStorage.getItem('scamlens_totals') || '{"n":0,"h":0}') } catch {}
  document.getElementById('analysisCount').textContent = t.n;
  document.getElementById('highCount').textContent = t.h;
}

async function analyze() {
  if (!currentUser) { go('login'); return }
  const text = document.getElementById('analysisText').value.trim();
  if (!text && !pendingImage) { alert('Please paste a message or upload a screenshot to analyze.'); return }

  if (currentPlan === 'free' && quotaLeft() <= 0) {
    document.getElementById('resultBox').style.display = 'block';
    document.getElementById('riskNum').textContent = '—';
    document.getElementById('riskLabel').textContent = 'LIMIT REACHED';
    document.getElementById('riskLabel').style.color = 'var(--muted)';
    document.getElementById('reasons').innerHTML = '<li>You\'ve used your ' + FREE_MONTHLY_LIMIT + ' free analyses this month. <a style="cursor:pointer;color:var(--blue)" onclick="go(\'pricing\')">Upgrade to Plus</a> for unlimited analyses.</li>';
    return;
  }

  const btn = document.querySelector('#analyzeCard .btn');
  busy(btn, 'Analyzing…');
  document.getElementById('resultBox').style.display = 'block';
  document.getElementById('riskNum').textContent = '…';
  document.getElementById('riskLabel').textContent = 'ANALYZING';
  document.getElementById('riskLabel').style.color = 'var(--muted)';
  document.getElementById('reasons').innerHTML = '<li>ScamLens AI is examining your message…</li>';

  let score, reasons, usedAI = true;
  try {
    const data = await aiAnalyze(text, pendingImage);
    score = data.score; reasons = data.reasons; usedAI = true;
  } catch (e) {
    const h = heuristicScore(text);
    score = h.score; reasons = h.reasons; usedAI = false;
  } finally {
    busy(btn, null);
  }

  if (!usedAI) reasons = reasons.concat(['Note: the AI service was unreachable, so this is a basic on-device check.']);
  const label = renderAnalysis(score, reasons);
  bumpTotals(score);
  const rawTitle = text ? text.slice(0, 42) + (text.length > 42 ? '…' : '') : 'Screenshot analysis';
  const safeTitle = rawTitle.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  pushHistory(score, label, safeTitle);
  if (currentPlan === 'free') { bumpQuota(); renderQuota(); }
}

/* ---------- settings ---------- */

function fillSettings() {
  if (!currentUser) return;
  const nameInput = document.getElementById('settingsName');
  if (nameInput && !document.activeElement?.matches?.('#settingsName')) {
    nameInput.value = currentUser.name || '';
  }
}

async function handleSettings(event) {
  event.preventDefault();
  const button = document.getElementById('settingsSubmit');
  const name = document.getElementById('settingsName').value.trim();
  setMsg('settings', '');
  busy(button, 'Saving…');
  try {
    const user = await updateUser({ full_name: name });
    applyUser(user);
    setMsg('settings', 'Your display name has been updated.', 'ok');
  } catch (error) {
    setMsg('settings', describeAuthError(error));
  } finally {
    busy(button, null);
  }
}

async function handlePasswordChange(event) {
  event.preventDefault();
  const button = document.getElementById('pwSubmit');
  const password = document.getElementById('pwNew').value;
  const confirmation = document.getElementById('pwConfirm').value;
  if (password.length < 8) { setMsg('pw', 'Choose a password of at least 8 characters.'); return }
  if (password !== confirmation) { setMsg('pw', 'The two passwords do not match.'); return }
  setMsg('pw', '');
  busy(button, 'Updating…');
  try {
    await updateUser({ password });
    document.getElementById('pwForm').reset();
    setMsg('pw', 'Your password has been updated.', 'ok');
  } catch (error) {
    setMsg('pw', describeAuthError(error));
  } finally {
    busy(button, null);
  }
}

/* ---------- billing (Stripe via Cloudflare Pages Functions) ---------- */

async function startCheckout(plan, btn) {
  if (!currentUser || !currentUser.email) {
    go('login');
    setMsg('login', 'Log in first, then choose your plan.', 'ok');
    return;
  }
  busy(btn, 'Redirecting…');
  try {
    const resp = await fetch(API_BASE + '/api/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, email: currentUser.email }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.error || 'Checkout failed. Please try again.');
    window.location.href = data.url;
  } catch (e) {
    alert(e.message);
  } finally {
    busy(btn, null);
  }
}

async function openPortal(btn) {
  if (!currentUser || !currentUser.email) return;
  busy(btn, 'Opening…');
  try {
    const resp = await fetch(API_BASE + '/api/portal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: currentUser.email }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.error || 'Could not open the billing portal.');
    window.location.href = data.url;
  } catch (e) {
    alert(e.message);
  } finally {
    busy(btn, null);
  }
}

async function refreshPlan() {
  const badge = document.getElementById('planBadge');
  const manage = document.getElementById('manageSub');
  if (!badge || !currentUser || !currentUser.email) return;
  try {
    const resp = await fetch(API_BASE + '/api/subscription?email=' + encodeURIComponent(currentUser.email));
    const data = await resp.json().catch(() => ({ plan: 'free' }));
    currentPlan = data.plan === 'pro' ? 'pro' : data.plan === 'plus' ? 'plus' : 'free';
    badge.textContent = data.plan === 'pro' ? 'Pro plan' : data.plan === 'plus' ? 'Plus plan' : 'Free plan';
    if (manage) manage.style.display = data.plan === 'free' ? 'none' : 'inline';
    renderQuota();
  } catch {
    currentPlan = 'free';
    badge.textContent = 'Free plan';
    if (manage) manage.style.display = 'none';
    renderQuota();
  }
}

/* ---------- boot ---------- */

function showNewPasswordForm(title, lead) {
  document.getElementById('resetTitle').textContent = title;
  document.getElementById('resetLead').textContent = lead;
  go('reset');
}

async function init() {
  initGoogle();
  document.getElementById('loginForm').addEventListener('submit', handleLogin);
  document.getElementById('signupForm').addEventListener('submit', handleSignup);
  document.getElementById('resetForm').addEventListener('submit', handleNewPassword);
  document.getElementById('settingsForm').addEventListener('submit', handleSettings);
  document.getElementById('pwForm').addEventListener('submit', handlePasswordChange);

  onAuthChange((event, user) => {
    if (event === AUTH_EVENTS.LOGOUT) {
      applyUser(null);
      resetDashboard();
      if (document.getElementById('dashboard').classList.contains('active')) go('home');
    } else if (user) {
      applyUser(user);
    }
  });

  // Consume any OAuth / confirmation / recovery / invite token in the URL hash
  // before restoring the session, so a returning visitor lands in one step.
  let landing = null;
  let notice = null;
  try {
    const result = await handleAuthCallback();
    if (result) {
      switch (result.type) {
        case 'oauth':
          notice = ['dash', 'Signed in with Google.', 'ok'];
          break;
        case 'confirmation':
          notice = ['dash', 'Email confirmed — your ScamLens account is active.', 'ok'];
          break;
        case 'email_change':
          notice = ['dash', 'Your email address has been updated.', 'ok'];
          break;
        case 'recovery':
          landing = 'recovery';
          break;
        case 'invite':
          inviteToken = result.token || null;
          landing = 'invite';
          break;
      }
    }
  } catch (error) {
    landing = 'login';
    notice = ['login', describeAuthError(error)];
  }

  applyUser(await getUser());
  paintHistory();
  paintTotals();

  // Stripe Checkout / portal return handling.
  const params = new URLSearchParams(window.location.search);
  if (params.get('checkout') === 'success') {
    window.history.replaceState({}, '', window.location.pathname);
    notice = ['dash', 'Payment successful — your plan is activating (this can take a few seconds).', 'ok'];
    setTimeout(() => refreshPlan(), 2000);
  } else if (params.get('checkout') === 'cancelled') {
    window.history.replaceState({}, '', window.location.pathname);
    go('pricing');
    return;
  } else if (params.get('portal') === 'return') {
    window.history.replaceState({}, '', window.location.pathname);
  }

  if (notice) setMsg(notice[0], notice[1], notice[2]);

  if (landing === 'recovery') {
    showNewPasswordForm('Choose a new password', 'Your reset link checked out. Set a new password to finish signing in.');
  } else if (landing === 'invite') {
    showNewPasswordForm('Accept your invitation', 'Set a password to activate your ScamLens account.');
  } else if (landing === 'login') {
    go('login');
  } else {
    go(currentUser ? 'dashboard' : 'home');
  }
}

/* ---------- chat assistant (smart fraud-prevention helper) ---------- */
const CHAT_SYSTEM = [
  'You are the ScamLens Assistant — an expert fraud-prevention specialist built into the ScamLens website, an AI-powered scam detection tool.',
  'YOUR EXPERTISE — use it fully:',
  '- Scam types: phishing/smishing/vishing, advance-fee scams ("processing fee", lottery, inheritance), romance scams, tech-support scams, fake job offers and task scams, crypto doubling and fake investment scams, marketplace and overpayment scams, rental scams, government impersonation (IRS, police, DMV), grandparent scams, QR-code and fake invoice scams.',
  '- Red flags: urgency and pressure ("act now", "final warning", "account suspended"), requests for gift cards, crypto or wire transfers, fees to claim a prize, too-good-to-be-true offers, mismatched sender addresses, threats of arrest or account closure, refusal to meet or video-call, pushing the chat off-platform, unsolicited links/attachments/QR codes, poor grammar in "official" messages, spoofed caller ID.',
  '- Verification playbook: stop — do not click, call back, or pay; verify independently through the official website or phone number (never the one in the message); inspect the real sender address; search the exact wording online; ask someone you trust; run the message through ScamLens analysis for a risk score.',
  '- If already scammed: cut all contact, send no more money, call the bank or card issuer immediately, change passwords, report it (US: reportfraud.ftc.gov and ic3.gov, plus local police), keep every screenshot and receipt as evidence.',
  'ABOUT SCAMLENS (answer site questions accurately): Free plan = 5 AI analyses per month, text analysis, risk score. Plus $7.99/mo = unlimited analyses, screenshot analysis, full history, priority AI. Pro $14.99/mo = everything in Plus plus advanced reports, link checking, priority support. Accounts live on the user\'s own device in the free version. Support email: umutgeo4@gmail.com.',
  'RULES:',
  '- Reply in the SAME language the user writes in (Turkish if they write Turkish, English if English). Default to English when unclear.',
  '- Be concise: 2-6 sentences or a short bullet list. Plain language, no jargon. Warm and direct, never preachy.',
  '- If the user pastes a suspicious message, link, or email, give a quick verdict — LOWER RISK, SUSPICIOUS, or HIGH RISK — with 2-4 short reasons and one clear next step.',
  '- NEVER ask for passwords, card numbers, SSNs, or verification codes. If the user shares one, warn them not to share it and do NOT repeat it back.',
  '- Never invent URLs, prices, or facts. If unsure, say so and recommend independent verification.',
  '- You cannot browse the web in real time; say so if asked about very recent events.',
].join('\n');

let chatHistory = [];
let chatBusy = false;
let chatGreeted = false;

function toggleChat() {
  const p = document.getElementById('chatPanel');
  const opening = p.classList.contains('hidden');
  p.classList.toggle('hidden');
  if (opening && !chatGreeted) {
    chatGreeted = true;
    addChatMsg('bot', "Hi, I'm the ScamLens Assistant. Ask me about suspicious messages, common scams, or how to stay safe — or paste a message here and I'll give you a quick risk read.");
  }
  if (opening) setTimeout(() => document.getElementById('chatText').focus(), 60);
}

function addChatMsg(role, text) {
  const box = document.getElementById('chatMsgs');
  const d = document.createElement('div');
  d.className = 'cmsg ' + (role === 'user' ? 'user' : 'bot');
  d.textContent = text;
  box.appendChild(d);
  box.scrollTop = box.scrollHeight;
  return d;
}

function sendChip(btn) {
  document.getElementById('chatText').value = btn.textContent;
  sendChat();
}

async function sendChat() {
  const input = document.getElementById('chatText');
  const text = input.value.trim();
  if (!text || chatBusy) return;
  const chips = document.getElementById('chatChips');
  if (chips) chips.style.display = 'none';
  addChatMsg('user', text);
  input.value = '';
  chatHistory.push({ role: 'user', content: text });
  chatBusy = true;
  const typing = addChatMsg('bot', 'Thinking…');
  typing.classList.add('typing');
  let reply;
  try {
    const resp = await fetch(AI_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'openai',
        messages: [{ role: 'system', content: CHAT_SYSTEM }, ...chatHistory.slice(-12)],
        temperature: 0.5,
        max_tokens: 600,
      }),
      signal: AbortSignal.timeout(45000),
    });
    if (!resp.ok) throw new Error('AI service returned HTTP ' + resp.status);
    const data = await resp.json();
    reply = ((data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) || '').trim();
    if (!reply) throw new Error('empty reply');
  } catch (e) {
    reply = "I'm having trouble reaching my brain right now. Try again in a moment — or paste the message into the Analyze box for a risk score.";
  }
  typing.textContent = reply;
  typing.classList.remove('typing');
  chatHistory.push({ role: 'assistant', content: reply });
  chatBusy = false;
  const box = document.getElementById('chatMsgs');
  box.scrollTop = box.scrollHeight;
}

Object.assign(window, {go, dashGo, goAnalyze, demo, logout, analyze, howItWorks, handleForgotPassword, handleImageSelect, startCheckout, openPortal, toggleChat, sendChat, sendChip, shareResult});

init();
